import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import patientService from '@/api/patientService';
import { toast } from 'sonner';

const ApplicationContext = createContext(null);

const ACTIVE_STATUSES = ['DRAFT', 'PENDING', 'UNDER_REVIEW'];
const LOCKED_STATUSES = ['APPROVED', 'REJECTED', 'UNDER_REVIEW'];

export function ApplicationProvider({ children }) {
  // Check if user is authenticated - define BEFORE useRef calls
  const isAuthenticated = () => {
    const token = localStorage.getItem('access_token');
    const userRole = localStorage.getItem('user_role');
    return !!(token && token !== 'undefined' && userRole?.toLowerCase() === 'patient');
  };

  const [currentApplication, setCurrentApplication] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Use Refs for internal tracking to avoid dependency loops in callbacks
  const isFetchingRef = useRef(false);
  const lastAuthState = useRef(isAuthenticated()); // Initialize with current auth state
  const hasHandledLogoutRef = useRef(false); // Track if logout has been handled to prevent loops
  const initializationAttemptedRef = useRef(false); // Track if we've attempted initialization

  // Check if application is locked (read-only)
  const isLocked = currentApplication
    ? LOCKED_STATUSES.includes(currentApplication.status?.toUpperCase())
    : false;

  // Check if application is editable
  const isEditable = currentApplication
    ? currentApplication.status?.toUpperCase() === 'DRAFT'
    : false;

  // Fetch user profile
  const fetchUserProfile = useCallback(async () => {
    if (!isAuthenticated()) return null;
    try {
      const data = await patientService.getProfile();
      setUserProfile(data);
      return data;
    } catch (error) {
      console.error('[ApplicationContext] Error fetching profile:', error);
      return null;
    }
  }, []);

  // Get current active application on mount/login
  const fetchCurrentApplication = useCallback(async () => {
    // Only fetch if user is authenticated
    if (!isAuthenticated()) {
      console.log('[ApplicationContext] User not authenticated, skipping fetch');
      setCurrentApplication(null);
      setIsLoading(false);
      setIsRefreshing(false);
      return null;
    }

    // Prevent duplicate calls using Ref
    if (isFetchingRef.current) {
      console.log('[ApplicationContext] Already fetching, skipping duplicate call');
      return null;
    }

    isFetchingRef.current = true;
    setIsRefreshing(true);
    try {
      const response = await patientService.getCurrentApplication();

      // API returns { success: true, data: { ... } } or { success: true, data: null }
      // Or direct data object
      let applicationData = null;

      if (response && typeof response === 'object') {
        if (response.data !== undefined) {
          applicationData = response.data; // { data: {...} } or { data: null }
        } else if (response.id) {
          applicationData = response; // Direct application object
        }
      }

      if (applicationData && applicationData.id) {
        setCurrentApplication(applicationData);
        sessionStorage.setItem('currentApplicationId', applicationData.id.toString());
        sessionStorage.setItem('currentApplicationStatus', applicationData.status || 'DRAFT');
        console.log('[ApplicationContext] Current application loaded:', applicationData.id);
        return applicationData;
      } else {
        // No active application
        setCurrentApplication(null);
        sessionStorage.removeItem('currentApplicationId');
        sessionStorage.removeItem('currentApplicationStatus');
        console.log('[ApplicationContext] No active application found');
        return null;
      }
    } catch (error) {
      console.error('[ApplicationContext] Error fetching current application:', error);

      // If 404, no application exists - this is OK
      if (error.response?.status === 404 || error.status === 404) {
        setCurrentApplication(null);
        sessionStorage.removeItem('currentApplicationId');
        sessionStorage.removeItem('currentApplicationStatus');
        return null;
      }

      // Other errors - don't show toast on mount, just log
      console.warn('[ApplicationContext] Failed to load application, will retry');
      return null;
    } finally {
      isFetchingRef.current = false;
      setIsRefreshing(false);
      // Ensure loading is cleared on first fetch completion
      setIsLoading(false);
    }
  }, []); // Empty dependency array as isAuthenticated relies on localStorage

  // Create or resume application
  const createOrResumeApplication = useCallback(async (applicationData) => {
    setIsLoading(true);
    try {
      const response = await patientService.createVisaApplication(applicationData);
      const appData = response?.data || response;

      if (appData && appData.id) {
        setCurrentApplication(appData);
        sessionStorage.setItem('currentApplicationId', appData.id.toString());
        sessionStorage.setItem('currentApplicationStatus', appData.status || 'DRAFT');
        console.log('[ApplicationContext] Application created/resumed:', appData);
        return appData;
      }
      throw new Error('Invalid response from server');
    } catch (error) {
      console.error('[ApplicationContext] Error creating/resuming application:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Get application by ID
  const getApplicationById = useCallback(async (applicationId) => {
    // Prevent duplicate calls
    if (isFetchingRef.current) {
      console.log('[ApplicationContext] Already fetching, skipping duplicate call');
      return currentApplication;
    }

    if (!isAuthenticated()) {
      console.log('[ApplicationContext] User not authenticated, skipping fetch');
      return null;
    }

    isFetchingRef.current = true;
    setIsRefreshing(true);
    try {
      const response = await patientService.getVisaApplicationById(applicationId);
      const appData = response?.data || response;

      if (appData && appData.id) {
        setCurrentApplication(appData);
        sessionStorage.setItem('currentApplicationId', appData.id.toString());
        sessionStorage.setItem('currentApplicationStatus', appData.status || 'DRAFT');
        return appData;
      }
      return null;
    } catch (error) {
      console.error('[ApplicationContext] Error fetching application by ID:', error);
      throw error;
    } finally {
      isFetchingRef.current = false;
      setIsRefreshing(false);
      // Ensure loading is cleared if this was the initialization fetch
      setIsLoading(false);
    }
  }, [currentApplication]);

  // Refresh current application
  const refreshApplication = useCallback(async () => {
    if (!currentApplication?.id) {
      return await fetchCurrentApplication();
    }
    return await getApplicationById(currentApplication.id);
  }, [currentApplication?.id, fetchCurrentApplication, getApplicationById]);

  // Clear application (on logout or completion)
  const clearApplication = useCallback(() => {
    setCurrentApplication(null);
    setUserProfile(null);
    sessionStorage.removeItem('currentApplicationId');
    sessionStorage.removeItem('currentApplicationStatus');
  }, []);

  // Initialize on mount/login - only if user is authenticated
  useEffect(() => {
    // Skip if already attempted initialization (unless isInitialized was reset)
    if (initializationAttemptedRef.current && isInitialized) {
      return;
    }

    const authenticated = isAuthenticated();

    // Mark that we've attempted initialization
    initializationAttemptedRef.current = true;

    // Check if user is authenticated before making any API calls
    if (!authenticated) {
      // User not authenticated - mark as initialized to prevent re-running
      if (!isInitialized) {
        console.log('[ApplicationContext] User not authenticated, skipping initialization');
        setIsLoading(false);
        setIsInitialized(true);
      }
      return;
    }

    // User is authenticated - initialize only once
    if (!isInitialized) {
      console.log('[ApplicationContext] Initializing application context');
      setIsInitialized(true);

      // Fetch profile data once
      fetchUserProfile();

      // Check sessionStorage first for quick load
      const storedId = sessionStorage.getItem('currentApplicationId');
      const storedStatus = sessionStorage.getItem('currentApplicationStatus');

      if (storedId && storedStatus && ACTIVE_STATUSES.includes(storedStatus.toUpperCase())) {
        // Try to fetch fresh data
        getApplicationById(parseInt(storedId)).catch((err) => {
          console.log('[ApplicationContext] Failed to fetch by ID, fetching current:', err);
          fetchCurrentApplication();
        });
      } else {
        fetchCurrentApplication();
      }

      // Safety timeout: Ensure loading is cleared even if network hangs
      const timeoutId = setTimeout(() => {
        setIsLoading(prev => {
          if (prev) console.warn('[ApplicationContext] Initialization slow, forcing loading false');
          return false;
        });
      }, 5000);

      return () => clearTimeout(timeoutId);
    }
  }, [isInitialized, fetchUserProfile, fetchCurrentApplication, getApplicationById]);

  // Listen for auth state changes (login/logout) - only handle logout, login is handled by initialization effect
  useEffect(() => {
    // Only listen for storage events (cross-tab logout) - don't poll
    const handleStorageChange = (e) => {
      if (e.key === 'access_token') {
        const authenticated = isAuthenticated();
        const wasAuthenticated = lastAuthState.current;

        // Only process if auth state actually changed
        if (wasAuthenticated === authenticated) {
          return; // No change, skip
        }

        // Update ref
        lastAuthState.current = authenticated;

        if (!authenticated && wasAuthenticated) {
          // User just logged out (transition from true to false)
          if (!hasHandledLogoutRef.current) {
            console.log('[ApplicationContext] User logged out (cross-tab), clearing application');
            clearApplication();
            setIsLoading(false);
            // setIsFetching(false); // This line was removed as setIsFetching is not defined
            initializationAttemptedRef.current = false; // Reset to allow re-initialization on login
            hasHandledLogoutRef.current = true;
          }
        } else if (authenticated && !wasAuthenticated) {
          // User just logged in (transition from false to true)
          console.log('[ApplicationContext] User just logged in (cross-tab), resetting initialization');
          initializationAttemptedRef.current = false; // Reset to allow re-initialization
          setIsInitialized(false); // Reset to trigger initialization effect
          hasHandledLogoutRef.current = false; // Reset logout flag
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [clearApplication]); // Empty dependencies - only run once on mount, use refs for state

  const value = React.useMemo(() => ({
    currentApplication,
    userProfile,
    applicationId: currentApplication?.id || null,
    applicationStatus: currentApplication?.status || null,
    isLocked,
    isEditable,
    isLoading,
    isRefreshing,
    fetchCurrentApplication,
    fetchUserProfile,
    createOrResumeApplication,
    getApplicationById,
    refreshApplication,
    clearApplication,
  }), [
    currentApplication,
    userProfile,
    isLocked,
    isEditable,
    isLoading,
    isRefreshing,
    fetchCurrentApplication,
    fetchUserProfile,
    createOrResumeApplication,
    getApplicationById,
    refreshApplication,
    clearApplication
  ]);

  return (
    <ApplicationContext.Provider value={value}>
      {children}
    </ApplicationContext.Provider>
  );
}

export function useApplication() {
  const context = useContext(ApplicationContext);
  if (!context) {
    throw new Error('useApplication must be used within ApplicationProvider');
  }
  return context;
}
